import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { DataTable } from '../../components/Tables';
import PageHeader from '../shared/PageHeader';
import ConfirmDeleteModal from '../../components/common/ConfirmDeleteModal';
import { clientsApi, extractList } from '../../api/facades';
import { clientTypeLabel, clientDisplayName } from './clientHelpers';

function mainContactName(r: any): string {
  const p = r?.people;
  if (p?.full_name) return p.full_name;
  if (p) return [p.first_name, p.middle_name, p.last_name].filter(Boolean).join(' ') || '—';
  return '—';
}

const ClientsListPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [isActive, setIsActive] = useState<'Yes' | 'No' | ''>('');
  const [clientType, setClientType] = useState<'single' | 'multiple' | ''>('');
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const { data: res, isLoading } = useQuery({
    queryKey: ['clients', search, isActive, clientType],
    queryFn: () =>
      clientsApi.list({
        search: search || undefined,
        isActive: isActive || undefined,
        clientType: clientType || undefined,
      }),
  });
  const rows = extractList(res);

  const deleteMu = useMutation({
    mutationFn: (id: number) => clientsApi.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      setDeleteId(null);
      toast.success('Client deleted.');
    },
    onError: (e: any) => toast.error(e?.message || 'Delete failed.'),
  });

  const columns = [
    { key: 'name', label: 'Name', render: (r: any) => clientDisplayName(r) || '—' },
    { key: 'client_type', label: 'Type', render: (r: any) => clientTypeLabel(r.client_type) },
    { key: 'main_contact', label: 'Main contact', render: (r: any) => mainContactName(r) },
    { key: 'email', label: 'Email', render: (r: any) => r?.people?.email ?? r?.email ?? '—' },
    { key: 'mobile', label: 'Mobile', render: (r: any) => r?.people?.mobile ?? r?.mobile ?? r?.phone ?? '—' },
    { key: 'is_active', label: 'Active', render: (r: any) => (r.is_active === 'Yes' ? 'Yes' : 'No') },
  ];

  return (
    <div>
      <PageHeader
        hideTitle
        subtitle="Manage client contacts and engagement."
        primaryAction={{ label: 'New Client', to: '/dashboard/clients/new' }}
      />
      <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6">
        <div className="mb-4 flex flex-wrap gap-4">
        <select
          value={isActive}
          onChange={(e) => setIsActive(e.target.value as 'Yes' | 'No' | '')}
          className="rounded-lg border border-gray-300 bg-white dark:bg-slate-800 dark:border-gray-600 py-2 px-3 text-sm"
        >
          <option value="">All</option>
          <option value="Yes">Active</option>
          <option value="No">Inactive</option>
        </select>
        <select
          value={clientType}
          onChange={(e) => setClientType(e.target.value as 'single' | 'multiple' | '')}
          className="rounded-lg border border-gray-300 bg-white dark:bg-slate-800 dark:border-gray-600 py-2 px-3 text-sm"
        >
          <option value="">All types</option>
          <option value="single">Individual</option>
          <option value="multiple">Corporate client</option>
        </select>
        <input
          type="search"
          placeholder="Search name, email, mobile"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="rounded-lg border border-gray-300 bg-white dark:bg-slate-800 dark:border-gray-600 py-2 px-3 text-sm w-48"
        />
      </div>
        <DataTable
          columns={columns}
          data={rows}
          loading={isLoading}
          emptyMessage="No clients."
          onRowClick={(id) => navigate(`/dashboard/clients/${id}`)}
          onEdit={(id) => navigate(`/dashboard/clients/${id}/edit`)}
          onDelete={(id) => setDeleteId(Number(id))}
        />
      </div>
      <ConfirmDeleteModal
        open={deleteId != null}
        onClose={() => setDeleteId(null)}
        onConfirm={() => deleteId != null && deleteMu.mutate(deleteId)}
        title="Delete client"
        message="Are you sure? This cannot be undone."
        loading={deleteMu.isPending}
      />
    </div>
  );
};

export default ClientsListPage;
